import type { RuleId, RuleSchemaEntry } from '../data/ruleSchema';
import type { RulePreset } from '../data/lotteryData';
import type { GameId, RulesCatalogResponse } from '../api/types';
import type { TicketRule } from '../types/rules';
import { getRuleBuilderState, resetRuleBuilderToStaticSeed, useRuleBuilderStore } from '../stores/ruleBuilderStore';
import type { PersistedRuleConfig } from '../rules/ruleConfigModel';
import type { SimpleVibeId } from '../rules/simpleVibeStacks';

/**
 * Facade for rule-builder data. UI should subscribe with `useRuleBuilderStore`;
 * use this object for mutations and future async I/O (local DB, remote API).
 */
export const ruleBuilderService = {
  /**
   * After sign-in, load `/v1/rules/catalog` so presets and field metadata match the Worker.
   * Preserves the current rule stack; on failure leaves static catalog.
   */
  async syncCatalogFromApi(fetchCatalog: () => Promise<RulesCatalogResponse>): Promise<void> {
    try {
      const { rules } = await fetchCatalog();
      const overlay: Partial<Record<RuleId, RuleSchemaEntry>> = {};
      const presets: RulePreset[] = [];
      for (const r of rules) {
        const id = r.kind as RuleId;
        overlay[id] = {
          info: r.info,
          stat: r.stat,
          defaults: r.defaults,
          fields: r.fields as RuleSchemaEntry['fields'],
        };
        presets.push({
          id,
          name: r.name,
          desc: r.description,
          icon: r.icon as RulePreset['icon'],
        } as RulePreset);
      }
      useRuleBuilderStore.setState({
        rulePresets: presets,
        ruleSchemaOverlay: overlay,
      });
    } catch {
      /* keep existing store */
    }
  },

  getPresets(): RulePreset[] {
    return getRuleBuilderState().rulePresets;
  },

  getRules(): TicketRule[] {
    return getRuleBuilderState().rules;
  },

  setActiveGame(gameId: GameId): void {
    getRuleBuilderState().setActiveGame(gameId);
  },

  setSimpleVibe(vibe: SimpleVibeId): void {
    getRuleBuilderState().setSimpleVibe(vibe);
  },

  addRule(id: RuleId): void {
    getRuleBuilderState().addRule(id);
  },

  removeRule(instanceId: string): void {
    getRuleBuilderState().removeRule(instanceId);
  },

  updateWeight(instanceId: string, weight: number): void {
    getRuleBuilderState().updateWeight(instanceId, weight);
  },

  updateRule(instanceId: string, patch: Partial<TicketRule>): void {
    getRuleBuilderState().updateRule(instanceId, patch);
  },

  /** Full replace for sync / import flows. */
  hydrate(payload: {
    rulePresets?: RulePreset[];
    rules?: TicketRule[];
    ruleSchemaOverlay?: Partial<Record<RuleId, RuleSchemaEntry>>;
  }): void {
    getRuleBuilderState().hydrate(payload);
  },

  hydrateRuleConfig(config: PersistedRuleConfig, options?: { activeGameId?: GameId }): void {
    getRuleBuilderState().hydrateRuleConfig(config, options);
  },

  exportRuleConfig(): PersistedRuleConfig {
    return getRuleBuilderState().exportRuleConfig();
  },

  /** Test / dev: restore module defaults without going through bootstrap. */
  resetToStaticDefaults(): void {
    resetRuleBuilderToStaticSeed();
  },
};
